import { z } from 'zod';
import { boardVisibilitySchema } from './board';
import { enterpriseGuestInviteRuleSchema } from './enterprise';
import { ulidSchema } from './id';
import { emailSchema, publicProfileSchema, usernameSchema } from './user';

/**
 * Workspaces and workspace membership — the contract layer for block B07
 * (`FR-PERM-004`, `FR-PERM-014`, `FR-PERM-021`).
 *
 * Narrow on purpose, exactly as `./user` is. `BE-02.1` — the full Phase 1
 * object model (board, list, card, comment, label, notification) — is a
 * different item and stays PENDING; what is here is what one shipped block
 * reads and writes and nothing more. A board schema added "while we are in the
 * neighbourhood" is how that item quietly becomes unreviewable.
 *
 * The file follows `./user`'s division and it is the same division for the
 * same reason: **input primitives**, which validate and normalise what a route
 * accepts, and **views**, which describe what a route returns. An input
 * primitive reused as a response field is the mistake both files are arranged
 * to prevent — a response schema that trims returns a name the database does
 * not hold, and one that rejects refuses to serve a row that legitimately
 * exists.
 *
 * WHAT IS DELIBERATELY ABSENT, so that its absence reads as a decision:
 *
 *   - `description`, `websiteUrl`, `logoUrl` and `workspaceType` are the rest
 *     of the workspace profile and belong to `FR-PERM-005` (V1, owned by
 *     `BE-39.1`). The columns exist — `0001_identity_and_workspaces.sql` — but
 *     no B07 route writes one, and serving a field no route can set invites
 *     the web client to bind to it and then discover it is always `null`.
 *   - `visibility` and `guestInviteRule` are `FR-PERM-006` and `FR-PERM-024`.
 *     Creation takes the column defaults (`'private'`, `'anyone'`) and B07
 *     exposes no control over either.
 *   - A workspace **invite link** shape. `FR-PERM-021`'s behaviour text names
 *     one, and forwards its rules to `FR-PERM-019`/`FR-PERM-020`, owned by
 *     `BE-38.3`. `workspace_invite_links` and `workspace_join_requests` are
 *     untouched by this block.
 *   - **Removing** a member (`FR-PERM-029`) and last-admin protection
 *     (`FR-PERM-032`, owned by `BE-10.3`). Demotion is built here; the rule
 *     that the last admin may not be demoted is not, and this file must not
 *     pretend otherwise by declaring a shape for it.
 *
 * Every width constant below is a real column width from
 * `db/postgres/migrations/0001_identity_and_workspaces.sql`. Getting one wrong
 * is not a type error — it is `value too long for type character varying(n)`
 * at INSERT time, surfaced as a generic failure, which is the class of mistake
 * this package exists to catch at the boundary.
 */

// ---------------------------------------------------------------------------
// Column widths. One place to change when a migration widens a column.
// ---------------------------------------------------------------------------

/** `workspaces.name varchar(255)`. */
export const WORKSPACE_NAME_MAX_LENGTH = 255;

/**
 * `workspaces.short_name varchar(64)`, `UNIQUE` globally
 * (`uq_workspaces_short_name`).
 *
 * Exported even though **no route accepts one**: it is derived at creation
 * (see `workspaceShortNameSchema`), and the server's derivation needs the
 * bound rather than restating a 64 that then disagrees with this one. Editing
 * a short name is `FR-PERM-005`, owned by `BE-39.1`.
 */
export const WORKSPACE_SHORT_NAME_MAX_LENGTH = 64;
export const WORKSPACE_DESCRIPTION_MAX_LENGTH = 1000;
export const WORKSPACE_WEBSITE_URL_MAX_LENGTH = 2048;
export const WORKSPACE_LOGO_URL_MAX_LENGTH = 2048;

export const WORKSPACE_TYPES = [
  'engineering',
  'marketing',
  'education',
  'sales',
  'support',
  'human_resources',
  'operations',
  'finance',
  'product_management',
  'design',
  'legal',
  'other',
] as const;

export const WORKSPACE_VISIBILITIES = ['public', 'private'] as const;

export const workspaceTypeSchema = z.enum(WORKSPACE_TYPES);
export const workspaceVisibilitySchema = z.enum(WORKSPACE_VISIBILITIES);

export type WorkspaceType = z.infer<typeof workspaceTypeSchema>;
export type WorkspaceVisibility = z.infer<typeof workspaceVisibilitySchema>;

export const WORKSPACE_GUEST_INVITE_RULES = [
  'anyone',
  'workspace_admins_only',
  'nobody',
] as const;

export const workspaceGuestInviteRuleSchema = z.enum(WORKSPACE_GUEST_INVITE_RULES);

export type WorkspaceGuestInviteRule = z.infer<typeof workspaceGuestInviteRuleSchema>;

// ---------------------------------------------------------------------------
// Roles — `FR-PERM-014`
// ---------------------------------------------------------------------------

/**
 * The two roles a workspace member holds.
 *
 * `FR-PERM-014`: "A workspace member holds one of two roles: **admin** or
 * **normal**." The tuple is the same closed set as
 * `ck_workspace_members_role`, and it is stated once here rather than at each
 * call site: a third value would be a runtime CHECK violation on INSERT, not a
 * compile error, exactly as an unregistered event type is.
 *
 * Note this is *not* the board role set — `ck_board_members_role` admits a
 * third value, `observer` (`FR-PERM-016`). The two role systems apply
 * simultaneously and are deliberately separate vocabularies; the capability
 * matrix above `FR-PERM-014` is the reason.
 *
 * Board roles landed in B09 and live in `./board` as `BOARD_ROLES`. **They stay
 * a second declaration rather than being merged with this one**, and the
 * asymmetry is the whole argument: a shared member shape would have to type its
 * `role` as the union of both CHECK constraints, which would let a workspace
 * route return `'observer'` — a runtime CHECK violation on
 * `ck_workspace_members_role` that no compiler would catch.
 */
export const WORKSPACE_ROLES = ['admin', 'normal'] as const;

export const workspaceRoleSchema = z.enum(WORKSPACE_ROLES);

export type WorkspaceRole = z.infer<typeof workspaceRoleSchema>;

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * A workspace name as `workspaces.name` stores it.
 *
 * Trimmed before it is measured, for the reason `displayNameSchema` gives:
 * trailing whitespace must not smuggle a name past the width bound, and a name
 * made only of spaces would otherwise become a blank entry in every member's
 * workspace switcher. `FR-PERM-004` requires "at minimum a name", so `.min(1)`
 * after the trim is the requirement, not a house preference.
 */
export const workspaceNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(WORKSPACE_NAME_MAX_LENGTH);

/**
 * A short name as `workspaces.short_name` holds it — a **read** bound, and the
 * bound the server's derivation is checked against.
 *
 * No charset regex, and that is a recorded gap rather than an omission:
 * `FR-PERM-005`'s edge case says "permitted short-name character set and
 * length are not documented publicly — verify before implementing validation".
 * Inventing one here would put product behaviour in the contract layer and
 * would then constrain `BE-39.1`, which owns the field. The derivation rule
 * that fills this column today lives with its only writer
 * (`apps/server/src/workspaces/short-name.ts`), the same division `./user`
 * uses for `users.username`.
 */
export const workspaceShortNameSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'Use lowercase letters, numbers and hyphens')
  .min(1)
  .max(WORKSPACE_SHORT_NAME_MAX_LENGTH);

export const workspaceDescriptionSchema = z
  .string()
  .trim()
  .min(1)
  .max(WORKSPACE_DESCRIPTION_MAX_LENGTH);

export const workspaceWebsiteUrlSchema = z
  .url({ protocol: /^https?$/ })
  .max(WORKSPACE_WEBSITE_URL_MAX_LENGTH);

export const workspaceLogoUrlSchema = z
  .url({ protocol: /^https?$/ })
  .max(WORKSPACE_LOGO_URL_MAX_LENGTH);

/**
 * `POST /workspaces` — `FR-PERM-004`, "supplying at minimum a name".
 *
 * A `strictObject`, like every body in `./user`: a plain `z.object()` in Zod 4
 * *strips* unknown keys rather than rejecting them, so
 * `{ name, shortName: 'mine' }` would return 200 having silently ignored the
 * short name the caller believes they chose — and choosing one is
 * `FR-PERM-005`, which this block does not build. The strict form makes that a
 * 422 naming the offending key.
 *
 * `name` is the only field. Everything else takes its column default; see the
 * "deliberately absent" note at the head of this file.
 */
export const createWorkspaceBodySchema = z.strictObject({
  name: workspaceNameSchema,
});

export type CreateWorkspaceBody = z.infer<typeof createWorkspaceBodySchema>;

/**
 * `POST /workspaces/:id/members` — `FR-PERM-021`: "Workspace admins add
 * members by email address or username".
 *
 * A **union of two strict objects, not one object with two optional fields**,
 * so the two handles are exclusive by construction: `{}` matches neither
 * branch and `{ email, username }` matches neither either, because
 * `strictObject` refuses the extra key. An object with two optionals would
 * make "neither supplied" and "both supplied, disagreeing about who to add"
 * runtime questions the handler has to answer — and the second one has no
 * right answer.
 *
 * Neither branch lower-cases its input, matching the note on `emailSchema`: a
 * schema that silently rewrote what it received would leave the wire shape and
 * the stored value disagreeing about what was sent. Case folding belongs at
 * the lookup, where the column's collation is the thing being compensated for.
 *
 * Adding a person who has **no account** is not this shape's business: it is
 * `FR-PERM-019`/`FR-PERM-020` (the shareable link and the pending invitation),
 * owned by `BE-38.3`. Until that exists the route answers 404 rather than
 * inventing a placeholder account.
 */
export const addWorkspaceMemberBodySchema = z.union([
  z.strictObject({ email: emailSchema }),
  z.strictObject({ username: usernameSchema }),
]);

export type AddWorkspaceMemberBody = z.infer<typeof addWorkspaceMemberBodySchema>;

/**
 * `PATCH /workspaces/:id/members/:userId/role` — SC-4, field-scoped.
 *
 * The role moves on its own and can move nothing else. Never a
 * `PUT /workspaces/:id/members/:userId` carrying the whole membership: an
 * absent key and a cleared value are the same thing in the body a PUT
 * receives, and `FR-EXP-031`'s per-field last-write-wins cannot be expressed
 * against it. The same rule the profile patches follow (`apps/server/README.md`
 * §3).
 */
export const workspaceRolePatchSchema = z.strictObject({
  role: workspaceRoleSchema,
});

export type WorkspaceRolePatch = z.infer<typeof workspaceRolePatchSchema>;

export const workspaceProfilePatchSchema = z.strictObject({
  name: workspaceNameSchema.optional(),
  shortName: workspaceShortNameSchema.optional(),
  description: workspaceDescriptionSchema.nullable().optional(),
  websiteUrl: workspaceWebsiteUrlSchema.nullable().optional(),
  logoUrl: workspaceLogoUrlSchema.nullable().optional(),
  workspaceType: workspaceTypeSchema.nullable().optional(),
});

export type WorkspaceProfilePatch = z.infer<typeof workspaceProfilePatchSchema>;

export const workspaceVisibilityPatchSchema = z.strictObject({
  visibility: workspaceVisibilitySchema,
});

export type WorkspaceVisibilityPatch = z.infer<typeof workspaceVisibilityPatchSchema>;

export const workspaceGuestInviteRulePatchSchema = z.strictObject({
  guestInviteRule: workspaceGuestInviteRuleSchema,
});

export type WorkspaceGuestInviteRulePatch = z.infer<
  typeof workspaceGuestInviteRulePatchSchema
>;

/**
 * `GET`/`PATCH /workspaces/:id/guest-invite-rule` — `FR-PERM-024`, and the
 * Enterprise half of `FR-PERM-035`.
 *
 * `guestInviteRule` is and stays the **workspace column**: the three-value set
 * `ck_workspaces_guest_invite_rule` admits, and exactly what the PATCH writes.
 * The two fields beside it describe the rule actually *enforced*, which under
 * an Enterprise is not the same value.
 *
 * **`effectiveGuestInviteRule` is typed on the FOUR-value Enterprise enum, and
 * it has to be.** The two CHECK constraints are asymmetric on purpose:
 *
 * ```
 * ck_enterprise_settings_guest_invite_rule  IN ('anyone','workspace_admins_only',
 *                                              'same_workspace_members','nobody')
 * ck_workspaces_guest_invite_rule           IN ('anyone','workspace_admins_only',
 *                                              'nobody')
 * ```
 *
 * `same_workspace_members` — the stricter tier `FR-PERM-035` names, which
 * "effectively disables guest access" — **exists at Enterprise level and
 * cannot be stored at workspace level**. So `WorkspaceGuestInviteRule` cannot
 * represent the answer, and typing the effective rule with it would make this
 * schema reject a value the server legitimately produced. The constraints are
 * the citation, not a preference.
 *
 * ⚠ **`undefined` and `null` are NOT the same fact on
 * `enterpriseGuestInviteRule`, and collapsing them is the mistake a later
 * reader will make.** `undefined` = *this response does not carry the field*.
 * `null` = *the workspace has no Enterprise link*, which is a positive answer.
 * A value = the Enterprise's own rule. A surface writing `?? 'anyone'` across
 * both erases the difference between "not told" and "told there is none".
 */
export const workspaceGuestInviteRuleSettingSchema = z.object({
  workspaceId: ulidSchema,
  /** `workspaces.guest_invite_rule` — the three-value workspace column. */
  guestInviteRule: workspaceGuestInviteRuleSchema,
  /**
   * `enterprise_settings.guest_invite_rule`, or `null` when the workspace has
   * no Enterprise. Absent when the response predates this field.
   */
  enterpriseGuestInviteRule: enterpriseGuestInviteRuleSchema.nullable().optional(),
  /**
   * The rule `evaluateGuestInvitePolicy` actually enforces — the more
   * restrictive of the workspace column and the Enterprise setting. Absent
   * means fall back to `guestInviteRule`.
   */
  effectiveGuestInviteRule: enterpriseGuestInviteRuleSchema.optional(),
});

export type WorkspaceGuestInviteRuleSetting = z.infer<
  typeof workspaceGuestInviteRuleSettingSchema
>;

export const BOARD_VISIBILITY_POLICY_RULES = [
  'any_member',
  'admins_only',
  'nobody',
] as const;

export const boardVisibilityPolicyRuleSchema = z.enum(BOARD_VISIBILITY_POLICY_RULES);

export type BoardVisibilityPolicyRule = z.infer<typeof boardVisibilityPolicyRuleSchema>;

/**
 * Where a **workspace-level** row came from — `'default'` when no
 * `workspace_board_visibility_policies` row exists, `'workspace'` when one
 * does.
 *
 * ⚠ **This enum's domain must not move, and `'enterprise'` must never be added
 * to it.** `WorkspaceBoardVisibilityPolicySection` attributes provenance with
 * a **binary ternary** — `source === 'workspace' ? 'Source: Workspace policy'
 * : 'Source: Default policy'` — so a third arm would render an
 * Enterprise-sourced row as *"Source: Default policy"*, the exact inversion of
 * the truth, with nothing red anywhere. Enterprise provenance is carried by
 * `workspaceEffectivePolicySourceSchema` on its own fields instead.
 */
export const workspaceBoardVisibilityPolicySourceSchema = z.enum([
  'default',
  'workspace',
]);

/**
 * Where the **effective** rule came from — the same two levels plus the
 * Enterprise.
 *
 * A separate declaration from `workspaceBoardVisibilityPolicySourceSchema`
 * rather than a widening of it, and the two are not interchangeable: swapping
 * them at a call site reintroduces the misrender documented above. This one is
 * only ever read by a surface that handles all three arms.
 */
export const workspaceEffectivePolicySourceSchema = z.enum([
  'default',
  'workspace',
  'enterprise',
]);

export type WorkspaceEffectivePolicySource = z.infer<
  typeof workspaceEffectivePolicySourceSchema
>;

/**
 * One row of `GET /workspaces/:id/board-visibility-policies` — `FR-PERM-013`,
 * with `FR-PERM-033`'s Enterprise attribution beside it.
 *
 * **Widened in place rather than extended on a copy, and the census is the
 * licence.** The house rule forbids widening because one schema usually
 * serialises several routes; this one serialises **exactly two** — the GET and
 * `PATCH …/board-visibility-policies/:visibility` — both in
 * `apps/server/src/routes/workspace-board-visibility-policies.ts`, both the
 * same subject. A copy would be strictly worse: `apps/web/src/lib/workspaces.ts`
 * parses the response with *this* schema, and Zod's `z.object` **strips**
 * unknown keys, so a wider shape returned under a copy would be silently
 * discarded before any surface saw it. Extending on a copy here would satisfy
 * the typechecker and deliver nothing.
 *
 * **`createRule` and `deleteRule` keep their exact meaning: the
 * workspace-level rule.** Making them *effective* would break the editing
 * surface — the radio's `value` is `policy.createRule`, so an admin editing
 * their own workspace rule under an Enterprise `nobody` would watch their
 * selection snap back and read it as a failed write. The effective values sit
 * beside them, named as such.
 *
 * Why the attribution matters at all: the **write** path already refuses on
 * the Enterprise rule (`boards.ts` calls `assertEnterpriseCreatePolicyAllows`
 * and `assertEnterpriseDeletePolicyAllows` after the workspace check), so
 * before these fields existed a workspace admin could *read* "any member" and
 * be answered 403 when they acted on it. The read was not merely unattributed;
 * it disagreed with the write.
 *
 * The five new fields are **`.optional()`** because this schema is already
 * shipped: four unowned files construct `WorkspaceBoardVisibilityPolicy`
 * object literals that would stop compiling, and an unowned test mocks the
 * response as a literal JSON constant that `api.ts`'s `safeParse` would then
 * reject at runtime. Optionality is a compatibility device here, **not a claim
 * that a handler may omit them** — both verbs populate all five, and because
 * `tsc` will no longer catch an omission, `workspace-admin.integration.test.ts`
 * asserts them on the GET *and* the PATCH.
 *
 * Restrictiveness order, stated once so it is not re-derived per call site:
 * `any_member < admins_only < nobody`. Ties resolve to `'workspace'` where the
 * workspace row exists, else `'enterprise'` where the Enterprise row binds,
 * else `'default'`.
 */
export const workspaceBoardVisibilityPolicySchema = z.object({
  visibility: boardVisibilitySchema,
  /** The **workspace-level** create rule — what the PATCH writes. */
  createRule: boardVisibilityPolicyRuleSchema,
  /** The **workspace-level** delete rule — what the PATCH writes. */
  deleteRule: boardVisibilityPolicyRuleSchema,
  /** Provenance of the workspace-level rules. Two arms, never three. */
  source: workspaceBoardVisibilityPolicySourceSchema,
  /**
   * The more restrictive of the workspace and Enterprise create rules —
   * `FR-PERM-033`'s edge case. Absent means fall back to `createRule`.
   */
  effectiveCreateRule: boardVisibilityPolicyRuleSchema.optional(),
  /** Same, for deletion. Absent means fall back to `deleteRule`. */
  effectiveDeleteRule: boardVisibilityPolicyRuleSchema.optional(),
  /** Which level `effectiveCreateRule` came from. Absent → fall back to `source`. */
  createRuleSource: workspaceEffectivePolicySourceSchema.optional(),
  /** Which level `effectiveDeleteRule` came from. Absent → fall back to `source`. */
  deleteRuleSource: workspaceEffectivePolicySourceSchema.optional(),
  /**
   * `enterprise_board_visibility_policies.workspace_admin_can_control`.
   *
   * ⚠ **THE POLARITY TRAP.** The column is `NOT NULL DEFAULT true`, so "no
   * Enterprise row" and "a permissive Enterprise row" are the same answer, and
   * **absent must read as permissive** — today's behaviour. A surface writing
   * `!policy.workspaceAdminCanControl` to mean "locked" locks every control
   * whenever the field is absent, producing a read-only policy page with
   * nothing red anywhere. **The predicate is
   * `policy.workspaceAdminCanControl !== false`.** `false` only ever means an
   * Enterprise admin deliberately set it.
   */
  workspaceAdminCanControl: z.boolean().optional(),
});

export type WorkspaceBoardVisibilityPolicy = z.infer<
  typeof workspaceBoardVisibilityPolicySchema
>;

export const workspaceBoardVisibilityPolicyListSchema = z.array(
  workspaceBoardVisibilityPolicySchema,
);

/**
 * `PATCH /workspaces/:id/board-visibility-policies/:visibility` — SC-4,
 * field-scoped.
 *
 * **`workspaceAdminCanControl` is deliberately NOT accepted here**, and the
 * absence is a decision rather than an oversight: that field belongs to the
 * Enterprise, `enterpriseBoardVisibilityPolicyPatchSchema` already owns
 * writing it, and the route already refuses a workspace admin who acts against
 * it (`assertEnterpriseAllowsWorkspacePolicyControl`, 403). Accepting it on
 * this body would let the governed level edit its own governor. The route's
 * order is unchanged and stays **404, then 403, then 409**.
 */
export const workspaceBoardVisibilityPolicyPatchSchema = z
  .strictObject({
    createRule: boardVisibilityPolicyRuleSchema.optional(),
    deleteRule: boardVisibilityPolicyRuleSchema.optional(),
  })
  .refine((body) => body.createRule !== undefined || body.deleteRule !== undefined, {
    message: 'At least one policy rule must be supplied',
  });

export type WorkspaceBoardVisibilityPolicyPatch = z.infer<
  typeof workspaceBoardVisibilityPolicyPatchSchema
>;

export const deleteWorkspaceBodySchema = z.strictObject({
  confirmName: workspaceNameSchema,
});

export type DeleteWorkspaceBody = z.infer<typeof deleteWorkspaceBodySchema>;

export const removeWorkspaceMemberBodySchema = z.strictObject({
  removeFromBoards: z.boolean().default(false),
});

export type RemoveWorkspaceMemberBody = z.infer<typeof removeWorkspaceMemberBodySchema>;

export const accountLifecycleBodySchema = z.strictObject({
  confirm: z.literal(true),
});

export type AccountLifecycleBody = z.infer<typeof accountLifecycleBodySchema>;

// ---------------------------------------------------------------------------
// Views — what a route RETURNS.
//
// Width-bounded strings, no format checks, no transforms — see the note above
// the input primitives, and `./user`'s longer version of the same argument.
// ---------------------------------------------------------------------------

/**
 * A workspace as `GET /workspaces`, `GET /workspaces/:id` and
 * `POST /workspaces` return it.
 *
 * Three fields, and the third one is here because it is externally meaningful
 * rather than incidental: `shortName` is what `FR-PERM-005` calls "used in the
 * workspace URL". It is derived at creation (decision 1 of the B07 brief) —
 * the column is `NOT NULL` and globally unique while `FR-PERM-004` asks the
 * creator for a name only — so returning it is how a caller learns the value
 * the server chose on their behalf.
 *
 * No `createdAt`. Nothing in `FR-PERM-004`, `FR-PERM-014` or `FR-PERM-021`
 * asks when a workspace was made, and a field on the wire is a field the web
 * client may bind to and a later block may not remove.
 */
export const workspaceSchema = z.object({
  id: ulidSchema,
  name: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
  shortName: z.string().max(WORKSPACE_SHORT_NAME_MAX_LENGTH),
  description: z.string().max(WORKSPACE_DESCRIPTION_MAX_LENGTH).nullable(),
  websiteUrl: z.string().max(WORKSPACE_WEBSITE_URL_MAX_LENGTH).nullable(),
  logoUrl: z.string().max(WORKSPACE_LOGO_URL_MAX_LENGTH).nullable(),
  workspaceType: workspaceTypeSchema.nullable(),
  visibility: workspaceVisibilitySchema,
});

export type Workspace = z.infer<typeof workspaceSchema>;

/**
 * One row of a workspace's member list — `FR-PERM-014` AC3: "when they view
 * the workspace member list, then they can see the roles of others".
 *
 * **Built by extending `publicProfileSchema`, deliberately.** That schema is
 * this repository's one definition of "the portions of a profile another
 * member may read" (`FR-PERM-002`), so a member list either serves exactly
 * those fields or it has invented a second, divergent public profile that will
 * drift from the first the next time either changes. Extending also makes the
 * leak impossible to introduce quietly: adding a column to the profile
 * projection cannot add `email` here, because `email` is not in the base.
 *
 * `id` is the member's **account** id (`users.id`), not a membership id —
 * `workspace_members` has no surrogate key, its primary key is
 * `(workspace_id, user_id)`. That is what lets a caller match a row back to
 * the person, and it is what `PATCH …/members/:userId/role` is addressed by.
 */
export const workspaceMemberSchema = publicProfileSchema.extend({
  /** `workspace_members.role`, `CHECK role IN ('admin','normal')`. */
  role: workspaceRoleSchema,
  /**
   * `workspace_members.created_at`, serialised as the driver returns it.
   *
   * A bounded string rather than `z.iso.datetime()` or a coerced `Date`: the
   * column is `timestamptz(3)` and `pg` hands it back in Postgres' own
   * spelling (`2026-08-10 12:00:00.123+00`), which is a legitimate instant and
   * not an ISO 8601 `T`/`Z` form. Pinning the serialisation here would make
   * this schema reject the value the database actually produced, and the claim
   * that matters — that it names a real instant — is asserted where it is
   * read, not where it is typed.
   *
   * Named `joinedAt` rather than `createdAt` because it is a fact about the
   * person, not about the row: "when did Grace join this workspace" is the
   * question a member list answers.
   */
  joinedAt: z.string().min(1),
  /**
   * `users.deactivated_at is not null` — `FR-PERM-034`: "An Enterprise admin
   * can **deactivate** a managed member, which revokes sign-in and all access
   * while leaving the account's authored content — comments, cards, activity
   * — intact and attributed."
   *
   * **The field has landed. The note that stood here said its absence was
   * blocked work rather than a decision against it, and that note is kept
   * verbatim below because it is the argument that got it shipped, not a
   * historical curiosity.**
   *
   * ⚠ **The predicate is `member.deactivated === true`, never
   * `!member.deactivated`** — the polarity reason
   * `workspaceBoardVisibilityPolicySchema.workspaceAdminCanControl` gives at
   * length. `undefined` here means *this projection does not say*, which is a
   * different claim from *this person is active*, and a negation folds the two
   * together. Optional, and deliberately **not** nullable: a projection that
   * has the fact states it as a boolean, and one that does not omits the key.
   * A SQL `NULL` must be mapped to `false`/absent at the projection, never
   * passed through — `null` is refused here so that mapping cannot be skipped
   * silently.
   *
   * **It is optional because a required field would not have shipped.** 26
   * files outside the chunk that added it name `WorkspaceMember`, eight of
   * them constructing object literals a required field would stop compiling.
   * Optional costs the compile-time net that would catch a missing
   * projection, so the missing projection is stated instead of implied:
   * **`apps/server/src/routes/workspaces.ts` does not select
   * `users.deactivated_at` yet.** All three routes that serialise this schema
   * go through the single function `toWorkspaceMember` there, so there is
   * exactly one place to add it, and until that happens the field is on the
   * wire contract and never on the wire.
   *
   * The original note, kept as written:
   *
   * > ⚠ **`deactivated` IS NOT HERE, AND ITS ABSENCE IS BLOCKED WORK RATHER
   * > THAN A DECISION AGAINST IT.**
   * >
   * > `FR-PERM-034` wants a deactivated workspace member's row to read as
   * > deactivated, and the Enterprise-administration contract designed the
   * > field as `deactivated: z.boolean().optional()` — optional as a
   * > compatibility device for the 26 unowned files naming `WorkspaceMember`,
   * > eight of which construct object literals a required field would stop
   * > compiling.
   * >
   * > **Optionality does not carry it, and that is the measurement worth
   * > keeping.** Two tests read this schema's key set *directly*, where
   * > `.optional()` is invisible:
   * >
   * >   - `packages/shared/src/board.test.ts:746–748` — asserts that
   * >     `Object.keys(workspaceMemberSchema.shape)` contains nothing the
   * >     board member view lacks, so any new key here makes the
   * >     board/workspace divergence read as two differences rather than one.
   * >     That assertion is an argument about why the two member shapes stay
   * >     separate declarations, not a mechanical count.
   * >   - `packages/shared/src/object-model.test.ts:378` — pins the exact
   * >     sorted nine-key list.
   * >
   * > Both were measured red against the field and **both are unowned by the
   * > chunk that designed it**, so the field is deferred rather than shipped
   * > behind an edit to somebody else's oracle. Whichever chunk owns those two
   * > files adds the key and moves both pins in one commit.
   *
   * Both pins moved with this key, in one commit, by the chunk that owns them:
   * the divergence assertion now reads `['deactivated']` in the
   * workspace-minus-board direction, and the key list is ten rather than nine.
   *
   * `deactivated` also ships on `enterpriseMemberSchema` (`./enterprise`),
   * which is the Enterprise-admin-scoped read `FR-PERM-034` actually grants
   * power over. This one is the same fact seen by a workspace audience, and
   * the two are separate declarations for the same reason the member views
   * are.
   *
   * **`managed` is deliberately NOT coming here, and that one IS a
   * decision.** `GET /workspaces/:id/members` is readable by any workspace
   * *member*, not only an admin (`FR-PERM-014` AC3 requires that), and
   * publishing which colleagues sit on a verified corporate domain would be an
   * Enterprise disclosure to a non-Enterprise audience. `FR-PERM-034` asks
   * nothing of this list; managed-ness stays behind `requireEnterpriseAdmin`.
   * Deactivation is different: `FR-PERM-034` AC3 keeps a deactivated person's
   * comments "visible and attributed", so the row stays in the list, and a
   * list that shows the row while refusing to say it is deactivated is the
   * defect rather than the discretion.
   */
  deactivated: z.boolean().optional(),
});

export type WorkspaceMember = z.infer<typeof workspaceMemberSchema>;

export const workspaceMemberListSchema = z.array(workspaceMemberSchema);

export const workspaceGuestTypeSchema = z.enum(['single_board', 'multi_board']);

export type WorkspaceGuestType = z.infer<typeof workspaceGuestTypeSchema>;

export const workspaceGuestSchema = publicProfileSchema.extend({
  boardCount: z.number().int().min(1),
  guestType: workspaceGuestTypeSchema,
});

export type WorkspaceGuest = z.infer<typeof workspaceGuestSchema>;

export const workspaceGuestListSchema = z.array(workspaceGuestSchema);

/**
 * `POST /workspaces/:id/members` — `FR-PERM-021` AC4: "when an admin adds them
 * again, then the add is a no-op and the existing membership is stated".
 *
 * Both halves of that sentence are in the shape. `added` is how a caller tells
 * "I added them" from "they were already here" — the two are both successes
 * and both return 200, so a status code cannot carry the difference — and
 * `member` is the membership *as it stands*, which on the no-op path is the
 * row that was already there, with the role it already had. A response that
 * merely acknowledged the request would leave an admin unable to see that a
 * promoted member was not silently reset to `normal`.
 */
export const addWorkspaceMemberResultSchema = z.object({
  added: z.boolean(),
  member: workspaceMemberSchema,
});

export type AddWorkspaceMemberResult = z.infer<typeof addWorkspaceMemberResultSchema>;
